import { setReplicaList, onFindFolderSuccess } from '../actions/internalActions';
import { getArchiveFolders } from '../actions/publicActions';
import getMailboxLoadingState from '../selectors/getMailboxLoadingState';
import findFolders from '../services/findFolders';
import getDefaultRequestOptions from '../services/getDefaultRequestOptions';
import getReplicaLists from '../services/getReplicaLists';
import MailboxLoadStateEnum from '../store/schema/MailboxLoadStateEnum';
import addArchiveMappingsInSessionStore from '../util/addArchiveMappingsInSessionStore';
import getDefaultAnchorMailboxForHeaders from '../util/getDefaultAnchorMailboxForHeaders';
import { logUsage } from 'owa-analytics';
import distinguishedFolderId from 'owa-service/lib/factory/distinguishedFolderId';
import FolderId from 'owa-service/lib/contract/FolderId';
import { orchestrator } from 'satcheljs';
import {
    ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
    ARCHIVE_FOLDERS_TREE_TYPE,
} from '../util/folderConstants';
import { setLoadingStateForMailbox, fetchArchiveDumpsterFolder } from '../actions/internalActions';
import { getUserConfiguration } from 'owa-session-store';

/**
 * An orchestrator that gets the archive folders.
 */
export default orchestrator(getArchiveFolders, async actionMessage => {
    // if loading is in progress or the folders have already been loaded we return
    const loadingState = getMailboxLoadingState(ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID);
    if (
        loadingState == MailboxLoadStateEnum.Loading ||
        loadingState == MailboxLoadStateEnum.Loaded
    ) {
        return;
    }

    // sets the loading state to 'Loading' for archive folder tree
    setLoadingStateForMailbox(ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID, MailboxLoadStateEnum.Loading);
    const findFolderResponse = await findFolders(
        distinguishedFolderId({ Id: ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID })
    );
    if (
        !findFolderResponse ||
        !findFolderResponse.RootFolder ||
        !findFolderResponse.RootFolder.Folders ||
        !findFolderResponse.RootFolder.ParentFolder
    ) {
        setLoadingStateForMailbox(
            ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
            MailboxLoadStateEnum.ErrorLoading
        );
        return;
    }

    onFindFolderSuccess(
        findFolderResponse.RootFolder,
        ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
        ARCHIVE_FOLDERS_TREE_TYPE,
        getDefaultAnchorMailboxForHeaders(),
        getUserConfiguration().SessionSettings.ArchiveDisplayName,
        ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID
    );

    // We are adding the archive root folder and its distinguished children in session store maps
    // This is to easily access folders like deleted items using folderNameToId like we do for primary root.
    addArchiveMappingsInSessionStore(findFolderResponse.RootFolder);

    fetchArchiveDumpsterFolder();
    logArchiveMailboxInitialLoad(actionMessage.source);

    const folderIds = findFolderResponse.RootFolder.Folders.map(folder => folder.FolderId);

    // retrieve the replicaList property which contains the mailbox guid where the folder lives
    // the folder may live in an aux archive mailbox in a different forest from the user's primary
    // archive mailbox
    getFolderReplicaLists(folderIds);
});

/**
 * Log the Archive Mailbox initial load.
 * @param source - Source of invocation. It can be Folder Pane or Move To
 */
function logArchiveMailboxInitialLoad(source: string) {
    logUsage('Triage_LoadArchiveMailboxFolders', [source]);
}

async function getFolderReplicaLists(folderIds: FolderId[]) {
    if (folderIds.length == 0) {
        return;
    }

    const getFolderResponseItems = await getReplicaLists(
        folderIds,
        getDefaultRequestOptions(ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID)
    );

    if (getFolderResponseItems) {
        getFolderResponseItems.forEach(item => {
            item.Folders.forEach(folder => {
                if (folder?.ReplicaList) {
                    setReplicaList(folder.FolderId.Id, folder.ReplicaList);
                }
            });
        });
    }
}
